import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { bool, node, string } from "prop-types";
import { useContext } from "react";

import IconButton from "~components/IconButton";
import { searchRoutes } from "~config/routing";
import { NavigationContext } from "~contexts/NavigationContext";
import { is750ThemeEnabled, useTheme } from "~contexts/ThemeContext";
import HeaderAuthButton from "~modules/HeaderAuthButton";
import IconButtonVisual from "~ts_components/Buttons/IconButtonVisual/IconButtonVisual";

import classes from "./Header.module.scss";

const FavouriteButton = dynamic(() => import("~components/FavouriteButton"));

const Header = ({
  leftContent,
  rightContent,
  subText,
  bottomLine = false,
  hideButtons = false,
  showFavouritesButton,
}) => {
  const { toggleTheme } = useTheme();
  const { locale, asPath } = useRouter();
  const { i18n } = useTranslation();
  const { currentDomain } = useContext(NavigationContext);

  const { t } = useTranslation();

  const searchRoute = searchRoutes.find((route) => route.locale === locale);

  const isPartyModeEnabled = is750ThemeEnabled(
    currentDomain,
    i18n.language,
    asPath,
  );

  // If the directory is not the root, we need to add it to the homepage link
  const currentHomepage = currentDomain?.landingPage?.[0]?.slug
    ? `/${currentDomain.landingPage[0].slug}`
    : "/";

  return (
    <header className={classes.header}>
      <div className={classes.content_wrapper}>
        {leftContent}
        <div className={classes.logo}>
          <Link href={currentHomepage} aria-label="Home">
            <Image
              src="/images/iamsterdam-logo.svg"
              alt="iAmsterdam logo"
              width={135}
              height={18}
            />
          </Link>
          {subText && <div className={classes.logo_text}>{subText}</div>}
        </div>
        {rightContent}
        {!hideButtons && (
          <div className={classes.mobile_favourite_button}>
            {isPartyModeEnabled && (
              <IconButton
                onClick={toggleTheme}
                icon="balloon"
                className={classes.theme}
              />
            )}
            <HeaderAuthButton />
            {showFavouritesButton && <FavouriteButton />}
            {/* Search button */}
            <Link href={searchRoute.path} title={t("search")}>
              <IconButtonVisual variant="outline" iconName="search" />
            </Link>
          </div>
        )}
      </div>
      {bottomLine && <div className={classes.bottom_line} />}
    </header>
  );
};

Header.propTypes = {
  subText: string,
  leftContent: node,
  rightContent: node,
  bottomLine: bool,
  hideButtons: bool,
  showFavouritesButton: bool,
};

export default Header;
